/**
 * Фильтры по столбцам таблиц раздела «Порты».
 *
 * Текстовые столбцы — поиск по вхождению без учёта регистра и разницы
 * «е/ё». Одиночный прочерк «-» (или «—») означает «значение не указано».
 *
 * Числовые столбцы понимают выражения:
 *   80     — ровно 80
 *   >80    — больше 80 (также <, >=, <=, =)
 *   20-80  — от 20 до 80 включительно
 * Знак «%» в конце допустим и ничего не меняет.
 */

/** Значение «не указано» в текстовом фильтре. */
export const EMPTY_MARK = /^[-—–]$/;

const norm = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .trim();

/**
 * Проверка текстового фильтра.
 * @param {unknown} value — значение ячейки (или массив значений: подходит любое)
 * @param {string} needle — текст фильтра
 */
export function textMatch(value, needle) {
  const n = String(needle ?? '').trim();
  if (!n) return true;
  const values = Array.isArray(value) ? value : [value];
  if (EMPTY_MARK.test(n)) return values.every((v) => !norm(v));
  const q = norm(n);
  return values.some((v) => norm(v).includes(q));
}

const NUM = String.raw`(\d+(?:[.,]\d+)?)`;
const RE_CMP = new RegExp(String.raw`^(>=|<=|=>|=<|>|<|=)?\s*${NUM}\s*%?$`);
const RE_RANGE = new RegExp(String.raw`^${NUM}\s*%?\s*(?:-|–|—|\.\.)\s*${NUM}\s*%?$`);
const toNum = (s) => Number(s.replace(',', '.'));

/**
 * Разбор числового выражения.
 * @returns {null | {ok: true, test: (n: number) => boolean} | {ok: false}}
 *   null — фильтр пуст; ok:false — выражение не распознано (подсвечиваем поле).
 */
export function parseNumFilter(input) {
  const s = String(input ?? '').trim();
  if (!s) return null;
  const r = s.match(RE_RANGE);
  if (r) {
    const [a, b] = [toNum(r[1]), toNum(r[2])].sort((x, y) => x - y);
    return { ok: true, test: (n) => n >= a && n <= b };
  }
  const m = s.match(RE_CMP);
  if (!m) return { ok: false };
  const v = toNum(m[2]);
  const op = { '=>': '>=', '=<': '<=' }[m[1]] || m[1] || '=';
  const tests = {
    '>': (n) => n > v,
    '<': (n) => n < v,
    '>=': (n) => n >= v,
    '<=': (n) => n <= v,
    '=': (n) => n === v,
  };
  return { ok: true, test: tests[op] };
}

/** Числовой фильтр: пустой или нераспознанный ничего не отсекает. */
export function numMatch(value, input) {
  const f = parseNumFilter(input);
  if (!f || !f.ok) return true;
  const n = Number(value);
  return Number.isFinite(n) && f.test(n);
}

/** Поле с нераспознанным выражением — для подсветки. */
export const isBadNum = (input) => parseNumFilter(input)?.ok === false;

/**
 * Фильтр объекта. Подпись в таблице — «MAG 134», но пишут и «MAG134»,
 * и просто «134»: подходят все три вида.
 */
export function objectMatch(label, mag, needle) {
  return textMatch([label, String(label ?? '').replace(/\s+/g, ''), mag], needle);
}

/** Пустой набор фильтров таблицы коммутаторов. */
export const EMPTY_SWITCH_FILTERS = Object.freeze({
  object: '',
  sw: '',
  cabinet: '',
  location: '',
  ports: '',
  vlans: '',
  devices: '',
});

/** Пустой набор фильтров таблицы устройств. */
export const EMPTY_DEVICE_FILTERS = Object.freeze({
  object: '',
  sw: '',
  port: '',
  vlan: '',
  mac: '',
  device: '',
  name: '',
  user: '',
});

export const hasAny = (filters) => Object.values(filters).some((v) => String(v).trim());

/**
 * Строка таблицы коммутаторов под фильтрами столбцов.
 * @param {object} s — строка: mag, sw, sw_ip, cabinet, location, total, up, vlans, devices
 * @param {object} f — фильтры (см. EMPTY_SWITCH_FILTERS)
 * @param {string} label — подпись объекта, как в таблице
 * @param {number} util — загрузка портов, %
 */
export function matchSwitchRow(s, f, label, util) {
  return (
    objectMatch(label, s.mag, f.object) &&
    textMatch([s.sw, s.sw_ip], f.sw) &&
    textMatch(s.cabinet, f.cabinet) &&
    textMatch(s.location, f.location) &&
    numMatch(util, f.ports) &&
    numMatch(s.vlans, f.vlans) &&
    numMatch(s.devices, f.devices)
  );
}

/**
 * Параметры запроса устройств по фильтрам столбцов. Объект сюда не
 * входит: он превращается в список объектов на стороне интерфейса.
 */
export function deviceFilterParams(f) {
  const map = {
    sw: 'f_sw',
    port: 'f_port',
    vlan: 'f_vlan',
    mac: 'f_mac',
    device: 'f_device',
    name: 'f_name',
    user: 'f_user',
  };
  const out = {};
  for (const [k, p] of Object.entries(map)) {
    const v = String(f[k] ?? '').trim();
    if (v) out[p] = v;
  }
  return out;
}
